/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useState } from 'react';
import {
  Entity,
  getEntitySourceLocation,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import {
  EntityRefLinks,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { JsonArray } from '@backstage/types';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import { MarkdownContent } from '@backstage/core-components';

import { AboutField } from './AboutField';
import { LinksGridList } from '../utils/EntityLinksCard/LinksGridList';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogTranslationRef } from '../utils/translation';
import { useBusinessUnitInfo } from './useBusinessUnitInfo';

const useStyles = makeStyles(() => ({
  description: {
    wordBreak: 'break-word',
  },
}));

/**
 * Props for {@link AboutContent}.
 *
 * @public
 */
export interface AboutContentProps {
  entity: Entity;
  appInventoryId?: string | undefined;
  internalId?: string | undefined;
  hideFields?: ('owner' | 'type' | 'lifecycle' | 'tags')[];
}

function getLocationTargetHref(
  target: string,
  type: string,
  entitySourceLocation: {
    type: string;
    target: string;
  },
): string {
  if (type === 'url' || target.includes('://')) {
    return target;
  }

  const srcLocationUrl =
    entitySourceLocation.type === 'file'
      ? `file://${entitySourceLocation.target}`
      : entitySourceLocation.target;

  if (type === 'file' || entitySourceLocation.type === 'file') {
    return new URL(target, srcLocationUrl).href;
  }

  return srcLocationUrl;
}
/**
 * Determines the correct display source for business unit assignment.
 * Shows "from LeanIX AppID" when AppID is valid, otherwise shows assumed source.
 */
function getBusinessUnitDisplaySource(
  entity: Entity,
  businessUnitInfo?: {
    resolvedBusinessUnit: string;
    fromLeanIX?: string;
    isValidAppId?: boolean;
  },
): {
  source: string;
  showInfoIcon: boolean;
  isDefaultNamespace?: boolean;
  isUnmappedOrg?: boolean;
} {
  const appId = entity.metadata?.labels?.['pfg-app-inventory-id'];
  const namespace = entity.metadata?.namespace?.toLowerCase();

  // Handle principalfinancialgroup-emu: is org, BU assigned via OrgToBUMapping
  if (
    namespace === 'principalfinancialgroup-emu' &&
    (!businessUnitInfo ||
      businessUnitInfo.resolvedBusinessUnit === 'Unknown Business Unit')
  ) {
    return {
      source: 'Could not determine from org mapping',
      showInfoIcon: true,
      isUnmappedOrg: true,
    };
  }

  // Handle principalmainframe-emu: is org, BU assigned via LOB code
  if (
    namespace === 'principalmainframe-emu' &&
    (!businessUnitInfo ||
      businessUnitInfo.resolvedBusinessUnit === 'Unknown Business Unit')
  ) {
    return {
      source: 'Could not determine from available data',
      showInfoIcon: true,
      isUnmappedOrg: true,
    };
  }

  // Handle default namespace: truly needs a GitHub org
  if (
    namespace === 'default' &&
    (!businessUnitInfo ||
      businessUnitInfo.resolvedBusinessUnit === 'Unknown Business Unit')
  ) {
    return {
      source: 'Could not determine - please add GitHub org',
      showInfoIcon: true,
      isDefaultNamespace: true,
    };
  }

  // If no AppID or it's 'no-entry', show assumed source
  if (!appId || appId === 'no-entry') {
    return {
      source: 'Assumed from namespace/GitHub org',
      showInfoIcon: true,
    };
  }

  // If no business unit info or unknown business unit, show assumed source
  if (
    !businessUnitInfo ||
    businessUnitInfo.resolvedBusinessUnit === 'Unknown Business Unit'
  ) {
    return {
      source: 'Assumed from namespace/GitHub org',
      showInfoIcon: true,
    };
  }

  // Use the validation result to determine the display source
  if (businessUnitInfo.isValidAppId === true) {
    return {
      source: 'from LeanIX AppID',
      showInfoIcon: false,
    };
  }

  return {
    source: 'Assumed from namespace/GitHub org',
    showInfoIcon: true,
  };
}

/** @public */
export function AboutContent(props: AboutContentProps) {
  const { entity } = props;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNamespaceModalOpen, setIsNamespaceModalOpen] = useState(false);
  const [isUnmappedOrgModalOpen, setIsUnmappedOrgModalOpen] = useState(false);
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslationRef(catalogTranslationRef);
  const {
    businessUnitInfo,
    loading: businessUnitLoading,
    error: businessUnitError,
  } = useBusinessUnitInfo(entity);

  const isSystem = entity.kind.toLocaleLowerCase('en-US') === 'system';
  const isResource = entity.kind.toLocaleLowerCase('en-US') === 'resource';
  const isComponent = entity.kind.toLocaleLowerCase('en-US') === 'component';
  const isAPI = entity.kind.toLocaleLowerCase('en-US') === 'api';
  const isTemplate = entity.kind.toLocaleLowerCase('en-US') === 'template';
  const isLocation = entity.kind.toLocaleLowerCase('en-US') === 'location';
  const isGroup = entity.kind.toLocaleLowerCase('en-US') === 'group';

  const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const partOfComponentRelations = getEntityRelations(
    entity,
    RELATION_PART_OF,
    {
      kind: 'component',
    },
  );
  const partOfDomainRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'domain',
  });
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

  let entitySourceLocation:
    | {
        type: string;
        target: string;
      }
    | undefined;
  try {
    entitySourceLocation = getEntitySourceLocation(entity);
  } catch (e) {
    entitySourceLocation = undefined;
  }

  return (
    <Grid container>
      <AboutField
        label={t('aboutCard.descriptionField.label')}
        gridSizes={{ xs: 12 }}
      >
        <MarkdownContent
          className={classes.description}
          content={
            entity?.metadata?.description ||
            t('aboutCard.descriptionField.value')
          }
        />
      </AboutField>
      {!props.hideFields?.includes('owner') && (
        <AboutField
          label={t('aboutCard.ownerField.label')}
          value={t('aboutCard.ownerField.value')}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          {ownedByRelations.length > 0 && (
            <EntityRefLinks entityRefs={ownedByRelations} defaultKind="group" />
          )}
        </AboutField>
      )}
      {(isSystem || partOfDomainRelations.length > 0) && (
        <AboutField
          label={t('aboutCard.domainField.label')}
          value={t('aboutCard.domainField.value')}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          {partOfDomainRelations.length > 0 && (
            <EntityRefLinks
              entityRefs={partOfDomainRelations}
              defaultKind="domain"
            />
          )}
        </AboutField>
      )}
      {(isAPI ||
        isComponent ||
        isResource ||
        partOfSystemRelations.length > 0) && (
        <AboutField
          label={t('aboutCard.systemField.label')}
          value={t('aboutCard.systemField.value')}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          {partOfSystemRelations.length > 0 && (
            <EntityRefLinks
              entityRefs={partOfSystemRelations}
              defaultKind="system"
            />
          )}
        </AboutField>
      )}
      {isComponent && partOfComponentRelations.length > 0 && (
        <AboutField
          label={t('aboutCard.parentComponentField.label')}
          value={t('aboutCard.parentComponentField.value')}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          <EntityRefLinks
            entityRefs={partOfComponentRelations}
            defaultKind="component"
          />
        </AboutField>
      )}
      {!props.hideFields?.includes('type') &&
        (isAPI ||
          isComponent ||
          isResource ||
          isTemplate ||
          isGroup ||
          isLocation ||
          typeof entity?.spec?.type === 'string') && (
          <AboutField
            label={t('aboutCard.typeField.label')}
            value={entity?.spec?.type as string}
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          />
        )}
      {!props.hideFields?.includes('lifecycle') &&
        (isAPI ||
          isComponent ||
          typeof entity?.spec?.lifecycle === 'string') && (
          <AboutField
            label={t('aboutCard.lifecycleField.label')}
            value={entity?.spec?.lifecycle as string}
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          />
        )}
      {!props.hideFields?.includes('tags') &&
        entity?.metadata?.tags &&
        entity?.metadata?.tags.length > 0 && (
          <AboutField
            label={t('aboutCard.tagsField.label')}
            value={t('aboutCard.tagsField.value')}
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          >
            {(entity?.metadata?.tags || []).map(tag => (
              <Chip key={tag} size="small" label={tag} />
            ))}
          </AboutField>
        )}
      {isLocation && (entity?.spec?.targets || entity?.spec?.target) && (
        <AboutField
          label={t('aboutCard.targetsField.label')}
          gridSizes={{ xs: 12 }}
        >
          <LinksGridList
            cols={1}
            items={((entity.spec.targets as JsonArray) || [entity.spec.target])
              .map(target => target as string)
              .map(target => ({
                text: target,
                href: getLocationTargetHref(
                  target,
                  (entity?.spec?.type || 'unknown') as string,
                  entitySourceLocation!,
                ),
              }))}
          />
        </AboutField>
      )}
      {isComponent && (
        <AboutField
          label="Namespace"
          value={entity?.metadata?.namespace || 'default'}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        />
      )}
      {props.appInventoryId &&
        (props.internalId !== 'Not Found' ? (
          <AboutField
            label="PFG App Inventory ID"
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          >
            <LinksGridList
              cols={1}
              items={[
                {
                  text: props.appInventoryId,
                  href: `https://principal.leanix.net/PrincipalProduction/factsheet/Application/${props.internalId}`,
                },
              ]}
            />
          </AboutField>
        ) : (
          <AboutField
            label="PFG App Inventory ID"
            value={props.appInventoryId}
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          />
        ))}
      {(isComponent || isSystem) && !businessUnitLoading && (
        <>
          {(() => {
            // Handle error state
            if (businessUnitError) {
              return (
                <AboutField
                  label="BUSINESS UNIT/Level2 ORG UNIT"
                  gridSizes={{ xs: 12, sm: 8, lg: 6 }}
                >
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#d32f2f' }}>
                      Error loading business unit
                    </div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                        marginTop: '2px',
                      }}
                    >
                      Unable to determine business unit. Please try refreshing
                      the page.
                    </div>
                  </div>
                </AboutField>
              );
            }

            if (isSystem && businessUnitInfo) {
              return (
                <AboutField
                  label="BUSINESS UNIT/Level2 ORG UNIT"
                  gridSizes={{ xs: 12, sm: 8, lg: 6 }}
                >
                  <div>
                    <div style={{ fontWeight: 'bold' }}>
                      {businessUnitInfo.resolvedBusinessUnit}
                    </div>
                  </div>
                </AboutField>
              );
            }

            if (isSystem && !businessUnitInfo) {
              return <></>;
            }

            // Handle missing data state (fallback to namespace assumption)
            if (!businessUnitInfo) {
              const namespace = entity.metadata?.namespace || 'default';
              let fallbackBusinessUnit: string;

              if (namespace.includes('principalris')) {
                fallbackBusinessUnit = 'Retirement And Income Solutions';
              } else if (namespace.includes('principalusis')) {
                fallbackBusinessUnit = 'Benefits And Protection';
              } else if (namespace.includes('principalinformationservices')) {
                fallbackBusinessUnit = 'Service Center Business Units';
              } else {
                fallbackBusinessUnit = 'Unknown Business Unit';
              }

              return (
                <AboutField
                  label="BUSINESS UNIT/Level2 ORG UNIT"
                  gridSizes={{ xs: 12, sm: 8, lg: 6 }}
                >
                  <div>
                    <div style={{ fontWeight: 'bold' }}>
                      {fallbackBusinessUnit}
                    </div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                        marginTop: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0px',
                      }}
                    >
                      <span>(Fallback from namespace)</span>
                      <IconButton
                        size="small"
                        onClick={() => setIsEditModalOpen(true)}
                        style={{
                          padding: '0px',
                          marginLeft: '-1px',
                          color: theme.palette.primary.main,
                        }}
                        aria-label="How to set an accurate business unit"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                </AboutField>
              );
            }

            if (entity.metadata?.labels?.['pfg-app-inventory-id']) {
              const {
                source: displaySource,
                showInfoIcon,
                isDefaultNamespace,
                isUnmappedOrg,
              } = getBusinessUnitDisplaySource(entity, businessUnitInfo);
              return (
                <AboutField
                  label="BUSINESS UNIT/Level2 ORG UNIT"
                  gridSizes={{ xs: 12, sm: 8, lg: 6 }}
                >
                  <div>
                    <div style={{ fontWeight: 'bold' }}>
                      {businessUnitInfo.resolvedBusinessUnit}
                    </div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                        marginTop: '2px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <span>({displaySource})</span>
                      {showInfoIcon && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (isDefaultNamespace) {
                              setIsNamespaceModalOpen(true);
                            } else if (isUnmappedOrg) {
                              setIsUnmappedOrgModalOpen(true);
                            } else {
                              setIsEditModalOpen(true);
                            }
                          }}
                          style={{
                            marginLeft: '-1px',
                            color: theme.palette.primary.main,
                          }}
                          aria-label="How to set an accurate business unit"
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                  </div>
                </AboutField>
              );
            }

            const {
              source: displaySource,
              showInfoIcon,
              isDefaultNamespace,
              isUnmappedOrg,
            } = getBusinessUnitDisplaySource(entity, businessUnitInfo);
            return (
              <AboutField
                label="BUSINESS UNIT/Level2 ORG UNIT"
                gridSizes={{ xs: 12, sm: 8, lg: 6 }}
              >
                <div>
                  <div style={{ fontWeight: 'bold' }}>
                    {businessUnitInfo.resolvedBusinessUnit}
                  </div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: theme.palette.text.secondary,
                      marginTop: '2px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <span>({displaySource})</span>
                    {showInfoIcon && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (isDefaultNamespace) {
                            setIsNamespaceModalOpen(true);
                          } else if (isUnmappedOrg) {
                            setIsUnmappedOrgModalOpen(true);
                          } else {
                            setIsEditModalOpen(true);
                          }
                        }}
                        style={{
                          marginLeft: '-1px',
                          color: theme.palette.primary.main,
                        }}
                        aria-label="How to set an accurate business unit"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                </div>
              </AboutField>
            );
          })()}
        </>
      )}

      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          style={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              style={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
              }}
            >
              Improving Business Unit Accuracy
            </Typography>
            <IconButton
              onClick={() => setIsEditModalOpen(false)}
              size="small"
              style={{
                color: theme.palette.text.secondary,
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent
          style={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            padding: '24px',
            margin: '8px 0',
          }}
        >
          <Typography paragraph style={{ marginBottom: '24px' }}>
            The business unit for this component is currently an assumption. Use
            one of the options below to get an accurate Level 2 Org Unit.
          </Typography>

          <Typography
            variant="subtitle1"
            style={{ fontWeight: 'bold', marginBottom: '8px' }}
          >
            Option 1 - Add LeanIX AppID
          </Typography>
          <Typography paragraph style={{ marginBottom: '16px' }}>
            Add your LeanIX Application ID to the <code>labels</code> section of
            your <code>catalog-info.yaml</code>. After merging, wait for
            scheduler to run in the portal.
          </Typography>
          <div
            style={{
              backgroundColor:
                theme.palette.type === 'dark'
                  ? theme.palette.grey[800]
                  : '#f5f5f5',
              padding: '16px 20px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              border: `1px solid ${
                theme.palette.type === 'dark' ? theme.palette.grey[600] : '#ddd'
              }`,
              marginBottom: '24px',
              whiteSpace: 'pre',
              color:
                theme.palette.type === 'dark'
                  ? theme.palette.grey[100]
                  : theme.palette.grey[800],
            }}
          >{`metadata:
  labels:
    pfg-app-inventory-id: appid-12345678`}</div>

          <Typography
            variant="subtitle1"
            style={{ fontWeight: 'bold', marginBottom: '8px' }}
          >
            Option 2 - Move to Correct GitHub Org
          </Typography>
          <Typography paragraph style={{ marginBottom: '24px' }}>
            If the business unit assigned to your component doesn't seem
            accurate, you can{' '}
            <a
              href="https://docs.github.com/en/repositories/creating-and-managing-repositories/transferring-a-repository#transferring-a-repository-owned-by-your-organization"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
              }}
            >
              move the repository
            </a>{' '}
            to the GitHub organization that aligns with the correct business
            unit.
          </Typography>

          <Typography
            variant="subtitle1"
            style={{ fontWeight: 'bold', marginBottom: '8px' }}
          >
            Option 3 - Need help? Reach out to PDP Team
          </Typography>
          <Typography paragraph style={{ marginBottom: '16px' }}>
            <a
              href="https://principal.service-now.com/esc?id=sc_cat_item&sys_id=cf39e5484711ed90291c87de536d4350&table=sc_cat_item&searchTerm=Principal%20Developer%20Portal"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
              }}
            >
              Open a ServiceNow request
            </a>{' '}
            with the PDP team and we&apos;ll help you determine the right
            approach.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isNamespaceModalOpen}
        onClose={() => setIsNamespaceModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          style={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              style={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
              }}
            >
              Add GitHub Organization to Namespace
            </Typography>
            <IconButton
              onClick={() => setIsNamespaceModalOpen(false)}
              size="small"
              style={{
                color: theme.palette.text.secondary,
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent
          style={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            padding: '24px',
            margin: '8px 0',
          }}
        >
          <Typography paragraph style={{ marginBottom: '24px' }}>
            To improve the accuracy of the business unit assignment, add the
            GitHub organization namespace to your <code>catalog-info.yaml</code>{' '}
            file.
          </Typography>

          <Typography
            variant="subtitle1"
            style={{ fontWeight: 'bold', marginBottom: '8px' }}
          >
            Add Namespace Section
          </Typography>
          <Typography paragraph style={{ marginBottom: '16px' }}>
            Add the <code>namespace</code> field under the <code>metadata</code>{' '}
            section of your <code>catalog-info.yaml</code> file with your GitHub
            organization name:
          </Typography>
          <div
            style={{
              backgroundColor:
                theme.palette.type === 'dark'
                  ? theme.palette.grey[800]
                  : '#f5f5f5',
              padding: '16px 20px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              border: `1px solid ${
                theme.palette.type === 'dark' ? theme.palette.grey[600] : '#ddd'
              }`,
              marginBottom: '24px',
              whiteSpace: 'pre',
              color:
                theme.palette.type === 'dark'
                  ? theme.palette.grey[100]
                  : theme.palette.grey[800],
            }}
          >{`metadata:
  namespace: principalinformationservices-emu`}</div>

          <Typography paragraph style={{ marginBottom: '16px' }}>
            Replace <code>principalinformationservices-emu</code> with your
            actual GitHub organization name. After merging this change, the
            business unit will be automatically determined based on your GitHub
            organization.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isUnmappedOrgModalOpen}
        onClose={() => setIsUnmappedOrgModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          style={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              style={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
              }}
            >
              Business Unit Cannot Be Determined
            </Typography>
            <IconButton
              onClick={() => setIsUnmappedOrgModalOpen(false)}
              size="small"
              style={{
                color: theme.palette.text.secondary,
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent
          style={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            padding: '24px',
            margin: '8px 0',
          }}
        >
          <Typography paragraph style={{ marginBottom: '24px' }}>
            {entity.metadata?.namespace?.toLowerCase() ===
            'principalmainframe-emu' ? (
              <>
                The business unit could not be determined from the repository
                name LOB code. For mainframe repositories, the business unit is
                typically extracted from the Line of Business (LOB) code in the
                repository name. You can view the{' '}
                <a
                  href="https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/components/#31-mainframe-lob-rule"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'underline',
                  }}
                >
                  complete list of LOB code mappings
                </a>{' '}
                to see which LOB codes are currently configured.
              </>
            ) : (
              <>
                The business unit could not be determined automatically. This
                GitHub organization does not have a business unit mapping
                configured in the system. You can view the{' '}
                <a
                  href="https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/components/#2-github-organization-rule"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'underline',
                  }}
                >
                  complete list of GitHub organization mappings
                </a>{' '}
                to see which organizations are currently configured.
              </>
            )}
          </Typography>

          <Typography paragraph style={{ marginBottom: '24px' }}>
            The business unit for this component is currently an assumption. Use
            one of the options below to get an accurate Level 2 Org Unit.
          </Typography>

          <Typography
            variant="subtitle1"
            style={{ fontWeight: 'bold', marginBottom: '8px' }}
          >
            Option 1 - Add LeanIX AppID
          </Typography>
          <Typography paragraph style={{ marginBottom: '16px' }}>
            Add your LeanIX Application ID to the <code>labels</code> section of
            your <code>catalog-info.yaml</code>. After merging, wait for
            scheduler to run in the portal.
          </Typography>
          <div
            style={{
              backgroundColor:
                theme.palette.type === 'dark'
                  ? theme.palette.grey[800]
                  : '#f5f5f5',
              padding: '16px 20px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              border: `1px solid ${
                theme.palette.type === 'dark' ? theme.palette.grey[600] : '#ddd'
              }`,
              marginBottom: '24px',
              whiteSpace: 'pre',
              color:
                theme.palette.type === 'dark'
                  ? theme.palette.grey[100]
                  : theme.palette.grey[800],
            }}
          >{`metadata:
  labels:
    pfg-app-inventory-id: appid-12345678`}</div>

          <Typography
            variant="subtitle1"
            style={{ fontWeight: 'bold', marginBottom: '8px' }}
          >
            Option 2 - Need help? Reach out to PDP Team
          </Typography>
          <Typography paragraph style={{ marginBottom: '16px' }}>
            <a
              href="https://principal.service-now.com/esc?id=sc_cat_item&sys_id=cf39e5484711ed90291c87de536d4350&table=sc_cat_item&searchTerm=Principal%20Developer%20Portal"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
              }}
            >
              Open a ServiceNow request
            </a>{' '}
            with the PDP team and we&apos;ll help you determine the right
            approach.
          </Typography>
        </DialogContent>
      </Dialog>
    </Grid>
  );
}
